/**
 * Today tab — the FSR's default landing on Appointments.
 *
 * Pulls from `/me/calendarView` (today only) and merges in any branch
 * Bookings appointments that fall today. Cards in chronological order.
 */

import { useMemo, useState } from 'react';
import { Pill, Button, KpiTile } from '@/components/primitives';
import { AppointmentCard } from '../components/AppointmentCard';
import { useTodayCalendar } from '../hooks/useTodayCalendar';
import { useBranchBookings } from '../hooks/useBranchBookings';
import type { Appointment } from '../types';

export interface TodayTabProps {
  onNewAppointment: () => void;
}

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const t = new Date();
  return d.toDateString() === t.toDateString();
}

export function TodayTab({ onNewAppointment }: TodayTabProps): JSX.Element {
  const cal = useTodayCalendar();
  const bookings = useBranchBookings();
  const [arrivedIds, setArrivedIds] = useState<ReadonlyArray<string>>([]);

  // Merge today's calendar items + today's branch bookings, dedupe by id, sort.
  const merged = useMemo<ReadonlyArray<Appointment>>(() => {
    const map = new Map<string, Appointment>();
    for (const a of cal.appointments) map.set(a.id, a);
    for (const a of bookings.appointments) {
      if (isToday(a.startUtc) && !map.has(a.id)) map.set(a.id, a);
    }
    const list = Array.from(map.values()).map((a) => (
      arrivedIds.includes(a.id) ? { ...a, arrived: true } : a
    ));
    list.sort((a, b) => a.startUtc.localeCompare(b.startUtc));
    return list;
  }, [cal.appointments, bookings.appointments, arrivedIds]);

  const counts = useMemo(() => {
    const total = merged.length;
    const teams = merged.filter((a) => a.channel === 'teams').length;
    const inBranch = merged.filter((a) => a.channel === 'inBranch').length;
    const phone = merged.filter((a) => a.channel === 'phone').length;
    return { total, teams, inBranch, phone };
  }, [merged]);

  const loading = cal.loading || bookings.loading;

  return (
    <section className="appt-tab" aria-label="Today's appointments">
      <div className="appt-tab__hero">
        <KpiTile label="Booked today" value={counts.total} />
        <KpiTile label="In-branch" value={counts.inBranch} />
        <KpiTile label="Teams meetings" value={counts.teams} />
        <KpiTile label="Phone" value={counts.phone} />
      </div>

      <div className="appt-tab__bar">
        <div>
          <h2>Today</h2>
          <span className="appt-tab__bar-sub">
            {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
          </span>
        </div>
        <Button variant="primary" onClick={onNewAppointment}>+ New appointment</Button>
      </div>

      {loading && (
        <div className="appt-tab__skeleton" role="status">Loading today's appointments…</div>
      )}

      {!loading && merged.length === 0 && (
        <div className="appt-empty">
          <Pill variant="muted">Nothing on today's calendar</Pill>
          <p>
            When customers book through Microsoft Bookings, or when you create an appointment
            here, cards show up on this page.
          </p>
          <Button variant="primary" onClick={onNewAppointment}>+ New appointment</Button>
        </div>
      )}

      {!loading && merged.length > 0 && (
        <ul className="appt-card-list" aria-label="Today's appointment cards">
          {merged.map((a) => (
            <li key={a.id}>
              <AppointmentCard
                appointment={a}
                onMarkArrived={(x) => setArrivedIds((prev) => (prev.includes(x.id) ? prev : [...prev, x.id]))}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
